import { LinearProgress } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { GetSupportedFiletypes } from "../../app/slices/AssetsSlice";
import { BrowseDialogViewMode } from "../../app/slices/GlobalSlice";
import { selectAvailableLanguages } from "../../app/slices/TrainingLocalizationSlice";
import PiBrowseListRow from "../../components/PiBrowseListRow";
import PiConfirmationDialog from "../../components/PiConfirmationDialog";
import PiDropRegion from "../../components/PiDropRegion";
import { AssetKey, AssetVariantDTO } from "../../dto/AssetDTO";
import useGridColumns from "../../hooks/useGridColumns";
import { useUploadVariant } from "../../hooks/UploadVariantHooks";
import VariantFileDropzone from "./VariantFileDropzone";
import VariantRowIcon from "./VariantRowIcon";
import VariantTile from "./VariantTile";
import { variantFileName, variantKind, variantLanguage } from "./variantDisplay";

/** Matched to the file explorer's grid so the two agree on cell size. */
export const VARIANT_TILE_MIN_WIDTH = 210;
export const VARIANT_TILE_GAP = 12;
export const DEFAULT_VARIANT_COLUMNS = 3;

interface VariantResultsProps {
  assetId: string;
  assetKey: AssetKey;
  /** Already ordered and filtered by the body. */
  variants: AssetVariantDTO[];
  viewMode: BrowseDialogViewMode;
  selectedIndex: number;
  onSelectIndex: (index: number) => void;
  /** Hidden while a search is running, like the file explorer's drop zone. */
  showDropzone: boolean;
  /**
   * Ties the results to the search field for assistive tech: the field's
   * `aria-activedescendant` points at one of these option ids.
   */
  listboxId: string;
  optionIdFor: (index: number) => string;
  /** Reports the measured column count so the arrow keys step a real row. */
  onColumnsChange: (columns: number) => void;
}

/**
 * The localize dialog's results column: the upload zone, then one cell per
 * localization as a row or a tile. Not virtualised — the list is bounded by
 * the number of languages, so there is nothing to page.
 */
export default function VariantResults(props: VariantResultsProps) {
  const {
    assetId,
    assetKey,
    variants,
    viewMode,
    selectedIndex,
    onSelectIndex,
    showDropzone,
    listboxId,
    optionIdFor,
    onColumnsChange,
  } = props;
  const { t } = useTranslation();
  const languages = useSelector(selectAvailableLanguages);

  // Uploading lives here rather than in the drop zone, because the zone is no
  // longer the only way in — the whole region takes a drop.
  const { handleFiles } = useUploadVariant();
  const supportedFileTypes = GetSupportedFiletypes(assetKey);
  const [showUnsupported, setShowUnsupported] = useState(false);

  const onFiles = useCallback(
    (files: FileList) => {
      const file = files[0];
      if (!file) return;
      if (!supportedFileTypes.includes(file.type)) {
        setShowUnsupported(true);
        return;
      }
      handleFiles(assetId, supportedFileTypes, file);
    },
    [assetId, supportedFileTypes, handleFiles]
  );

  const gridRef = useRef<HTMLDivElement | null>(null);
  const measured = useGridColumns(gridRef, {
    minCellWidth: VARIANT_TILE_MIN_WIDTH,
    gap: VARIANT_TILE_GAP,
    fallback: DEFAULT_VARIANT_COLUMNS,
    max: 5,
  });
  const columns = viewMode === "tile" ? measured : 1;

  useEffect(() => {
    onColumnsChange(columns);
  }, [columns, onColumnsChange]);

  const selectedRef = useRef<HTMLElement | null>(null);
  const setSelectedRef = useCallback((node: HTMLElement | null) => {
    selectedRef.current = node;
  }, []);

  // The arrows can move the highlight outside the scroll window.
  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex, viewMode]);

  const dropzone = showDropzone ? (
    <VariantFileDropzone
      onFiles={onFiles}
      accept={supportedFileTypes.toString()}
      variant={viewMode === "tile" ? "card" : "banner"}
    />
  ) : null;

  const row = (variant: AssetVariantDTO, index: number) => {
    const kind = variantKind(variant, languages);
    const fileName = variantFileName(variant);

    let title: string;
    let subtitle: React.ReactNode;
    let trailing: React.ReactNode;

    if (kind === "loading") {
      title = t("localize_ui.uploading");
      subtitle = (
        <span className="block w-[120px] pt-[6px]">
          <LinearProgress />
        </span>
      );
      trailing = undefined;
    } else if (kind === "default") {
      title = t("default");
      subtitle = t("localize_ui.default_description");
      // An em dash, not a code: the default deliberately has no language.
      trailing = (
        <span className="text-[11px] text-creator-text-soft" aria-hidden>
          —
        </span>
      );
    } else if (kind === "assigned") {
      title = variantLanguage(variant, languages)?.full ?? "";
      subtitle = fileName;
      trailing = (
        <span className="text-[11px] uppercase text-creator-text-sub">
          {variant.variant}
        </span>
      );
    } else {
      title = fileName ?? t("localize_ui.new_localization");
      subtitle = t("localize_ui.not_assigned_yet");
      trailing = (
        <span className="flex items-center gap-[6px]">
          {/* The amber carries the state; the label stays readable text. */}
          <span
            aria-hidden
            className="w-[6px] h-[6px] rounded-full bg-creator-notice"
          />
          <span className="text-[11px] text-creator-notice-text">
            {t("localize_ui.not_set")}
          </span>
        </span>
      );
    }

    return (
      <PiBrowseListRow
        key={variant.id}
        ref={index === selectedIndex ? setSelectedRef : undefined}
        icon={<VariantRowIcon variant={variant} assetKey={assetKey} />}
        title={title}
        subtitle={subtitle}
        trailing={trailing}
        optionId={optionIdFor(index)}
        isSelected={index === selectedIndex}
        onSelect={() => kind !== "loading" && onSelectIndex(index)}
        onConfirm={() => kind !== "loading" && onSelectIndex(index)}
      />
    );
  };

  const empty = variants.length === 0 && (
    <div className="py-[24px] text-center text-[13px] text-creator-text-sub">
      {t("localize_ui.no_results")}
    </div>
  );

  const listboxProps = {
    id: listboxId,
    role: "listbox" as const,
    "aria-label": t("localize_ui.results_label") ?? "",
    className:
      "flex-1 min-h-0 overflow-y-auto pit-quiet-scrollbar px-[10px] py-[8px]",
    "data-testid": "variant-results",
  };

  // The region wraps the scroller rather than sitting inside it, so the
  // overlay covers the visible area instead of the scrolled content.
  const withDropRegion = (content: JSX.Element) => (
    <>
      <PiDropRegion
        onFiles={onFiles}
        label={t("localize_ui.drop_here")}
        className="flex-1 min-h-0 flex flex-col"
        dataTestId="variant-drop-region"
      >
        {content}
      </PiDropRegion>

      <PiConfirmationDialog
        open={showUnsupported}
        onClose={() => setShowUnsupported(false)}
        title={t("localize_ui.unsupported_title")}
        text={t("localize_ui.unsupported_text")}
        cancelText="OK"
        confirmButtonVariant="piRed"
        confirmText="OK"
        onConfirm={() => setShowUnsupported(false)}
        height="225px"
        hideCancelButton
      />
    </>
  );

  if (viewMode === "tile") {
    return withDropRegion(
      <div {...listboxProps}>
        {/* The padding sits on the wrapper so the measured element's width is
            the width actually available to cells. */}
        <div className="px-[6px]">
          <div
            ref={gridRef}
            className="grid"
            style={{
              gap: VARIANT_TILE_GAP,
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            }}
          >
            {/* The drop zone leads the grid; anything appended after the
                localizations would sit below the fold in a long list. */}
            {dropzone && <div className="h-[172px]">{dropzone}</div>}
            {variants.map((variant, index) => (
              <div
                key={variant.id}
                ref={index === selectedIndex ? setSelectedRef : undefined}
              >
                <VariantTile
                  variant={variant}
                  assetKey={assetKey}
                  optionId={optionIdFor(index)}
                  isSelected={index === selectedIndex}
                  onSelect={() => onSelectIndex(index)}
                />
              </div>
            ))}
          </div>
        </div>
        {empty}
      </div>
    );
  }

  return withDropRegion(
    <div {...listboxProps}>
      {dropzone && <div className="px-[8px] pt-[2px] pb-[12px]">{dropzone}</div>}

      <div className="flex flex-col gap-[2px]">
        {variants.map((variant, index) => row(variant, index))}
      </div>

      {empty}
    </div>
  );
}
